<template>
  <div class="scroll-tab-area">
    <div class="app-tab-wrap">
      <button
        type="button"
        class="tab"
        v-for="region in REGION_FILTERS"
        :key="region.value"
        :class="{ on: region.value === regionFilter }"
        @click="handleRegionClick(region)"
      >
        {{ t(region.label) }}
      </button>
    </div>
  </div>

  <ul class="sel-lang line-sel-wrap">
    <li
      v-for="language in languageList"
      :key="language.code"
      :class="{ on: selectedLanguage?.code === language.code, disabled: excludedLanguages.includes(language.code) }"
      @click="handleLanguageChange(language)"
    >
      <span>{{ language.name }}</span>
      <IconCheck></IconCheck>
    </li>
    <li class="last-txt">🌎 {{ t('prepare_more_languages') }}</li>
  </ul>
</template>

<script setup lang="ts" generic="T = unknown">
import { computed, onMounted, ref } from 'vue';

import IconCheck from '@/components/jh-app/icons/IconCheck.vue';
import { listLanguage, REGION_FILTERS } from '@/static/languages.js';
import { Language } from '@/types/common';
import { RegionFilter } from '@/types/components';

const props = withDefaults(
  defineProps<{
    showAllList?: boolean;
    excludedLanguages?: string[];
    payload?: Language | null;
  }>(),
  {
    showAllList: false,
    excludedLanguages: () => [],
  }
);
const emit = defineEmits<{ confirm: [language: Language]; close: [] }>();

const { t } = useI18n();

const selectedLanguage = ref<Language | null>(null);
const regionFilter = ref<RegionFilter>('all');

const languageList = computed(() => {
  let filteredList = listLanguage;

  if (regionFilter.value !== 'all') {
    filteredList = filteredList.filter((lang) => lang.type.includes(regionFilter.value));
  }

  return filteredList;
});

const handleRegionClick = (region) => {
  regionFilter.value = region.value;
};

const handleLanguageChange = (language: Language) => {
  selectedLanguage.value = language;
  emit('confirm', language);
  emit('close');
};

onMounted(() => {
  selectedLanguage.value = props.payload ?? null;
});
</script>

<style scoped lang="scss">
.app-tab-wrap {
  padding: 1rem 0 1.5rem;
  overflow-x: hidden;
  touch-action: pan-y;
  -webkit-overflow-scrolling: auto;
}
.line-sel-wrap {
  max-height: 50vh;
  min-height: 50vh;
  overflow: auto;
  &::-webkit-scrollbar {
    display: none;
  }
  li {
    padding: 1rem 0;
    border-bottom: 1px solid $color-gray-40;
    display: flex;
    justify-content: space-between;
    span {
      font-size: $font-size-psmall;
    }
    svg {
      color: $color-navy-100;
      opacity: 0;
    }
    &.on {
      span {
        color: $color-navy-100;
      }
      svg {
        opacity: 1;
      }
    }
    &.last-txt {
      font-size: $font-size-psmall;
      justify-content: center;
      border-bottom: 0;
    }
    &.disabled {
      pointer-events: none;
      cursor: not-allowed;
      color: $color-gray-90;
    }
  }
}
.close-modal-wrap {
  .head {
    padding: 1rem 0;
    display: flex;
    justify-content: flex-end;
    .modal-close {
      padding: $size-xs;
    }
  }
}
</style>
